import { useState, useEffect } from 'react';
import { CheckCircle, RefreshCw, Users, BarChart3, AlertCircle, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import decagonService, { DecagonConfig, DecagonIntegrationStatus } from '../../services/decagon.service';
import DogLoader from '../ui/DogLoader';
import { useAuth } from '../../contexts/AuthContext';

export default function DecagonIntegration() {
  const { user } = useAuth();
  const [status, setStatus] = useState<DecagonIntegrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Form state
  const [apiKey, setApiKey] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  const organizationId = user?.organizationId || '';

  useEffect(() => {
    if (organizationId) {
      loadStatus();
    }
  }, [organizationId]);

  const loadStatus = async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      const statusData = await decagonService.getStatus(organizationId);
      setStatus(statusData);
    } catch (err) {
      console.error('Failed to load Decagon status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!organizationId) {
      setError('Organization ID not found. Please log in again.');
      return;
    }

    setConnecting(true);

    try {
      const config: DecagonConfig = {
        apiKey,
        workspaceId,
      };

      console.log('Connecting Decagon with organizationId:', organizationId);
      const result = await decagonService.connect(organizationId, config);

      if (result.success) {
        setShowConnectForm(false);
        setApiKey('');
        setWorkspaceId('');
        await loadStatus();
      } else {
        setError(result.message || 'Failed to connect to Decagon');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to connect to Decagon');
    } finally {
      setConnecting(false);
    }
  };

  const handleSyncAgents = async () => {
    setError('');
    setSyncing(true);

    try {
      const result = await decagonService.syncAgents(organizationId);

      if (result.success) {
        alert('Successfully synced agents from Decagon!');
        await loadStatus();
      } else {
        setError(result.message || 'Failed to sync agents');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to sync agents');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Decagon?')) {
      return;
    }

    try {
      await decagonService.disconnect(organizationId);
      await loadStatus();
    } catch (err) {
      setError('Failed to disconnect');
    }
  };

  if (loading) {
    return <DogLoader />;
  }

  return (
    <div className="space-y-4">
      {/* Connection Status */}
      <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
        <div
          className="p-4 hover:bg-gray-50 transition-all cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-4xl">🤖</div>
              <div>
                <div className="font-semibold text-gray-900 flex items-center">
                  Decagon
                  <Sparkles className="w-4 h-4 ml-2 text-purple-500" />
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Connect to sync AI agents and pull conversation analytics
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {status?.connected && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Connected
                </span>
              )}
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {/* Connection Details */}
          {status?.connected && !isExpanded && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Workspace ID:</span>
                  <span className="ml-2 font-medium text-gray-900">{status.config?.workspaceId}</span>
                </div>
                {status.lastSyncAt && (
                  <div>
                    <span className="text-gray-600">Last Sync:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {new Date(status.lastSyncAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4" onClick={(e) => e.stopPropagation()}>
            {/* Connection Details */}
            {status?.connected && (
              <div>
                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <span className="text-gray-600">Workspace ID:</span>
                    <span className="ml-2 font-medium text-gray-900">{status.config?.workspaceId}</span>
                  </div>
                  {status.lastSyncAt && (
                    <div>
                      <span className="text-gray-600">Last Sync:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {new Date(status.lastSyncAt).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex space-x-3 mb-4">
                  <button
                    onClick={() => setShowConnectForm(true)}
                    className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-white"
                  >
                    Reconfigure
                  </button>
                  <button
                    onClick={handleDisconnect}
                    className="px-4 py-2 border-2 border-red-300 text-red-700 rounded-lg font-medium hover:bg-red-50"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            )}

            {!status?.connected && !showConnectForm && (
              <button
                onClick={() => setShowConnectForm(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
              >
                Connect to Decagon
              </button>
            )}

            {/* Connection Form */}
            {showConnectForm && (
              <div className="border-2 border-purple-200 rounded-lg p-6 bg-white">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Configure Decagon Integration
                </h3>

                <form onSubmit={handleConnect} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Workspace ID
                    </label>
                    <input
                      type="text"
                      value={workspaceId}
                      onChange={(e) => setWorkspaceId(e.target.value)}
                      placeholder="your-workspace-id"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                      required
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Your Decagon workspace identifier
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API Key
                    </label>
                    <input
                      type="password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="Enter your Decagon API key"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                      required
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Get this from Settings → API in your Decagon dashboard
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowConnectForm(false);
                        setError('');
                      }}
                      className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                      disabled={connecting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      disabled={connecting}
                    >
                      {connecting ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        'Connect to Decagon'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Actions (only show when connected and not showing form) */}
            {status?.connected && !showConnectForm && (
              <div className="border-2 border-gray-200 rounded-lg p-4 bg-white">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>

                <div className="grid grid-cols-2 gap-4">
                  {/* Sync Agents */}
                  <button
                    onClick={handleSyncAgents}
                    disabled={syncing}
                    className="flex items-center justify-center px-4 py-3 border-2 border-purple-600 text-purple-600 rounded-lg font-medium hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {syncing ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Users className="w-5 h-5 mr-2" />
                        Sync Team Members
                      </>
                    )}
                  </button>

                  {/* Fetch Metrics */}
                  <button
                    className="flex items-center justify-center px-4 py-3 border-2 border-green-600 text-green-600 rounded-lg font-medium hover:bg-green-50"
                    onClick={() => alert('Fetch metrics functionality - coming soon!')}
                  >
                    <BarChart3 className="w-5 h-5 mr-2" />
                    Fetch AI Analytics
                  </button>
                </div>

                <div className="mt-4 bg-purple-50 border border-purple-200 rounded p-3">
                  <p className="text-sm text-purple-900">
                    <span className="font-medium">Tip:</span> Use "Sync Team Members" to import your Decagon AI agents into Lentil WFM.
                    Use "Fetch AI Analytics" to pull conversation metrics including AI-assisted interactions.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}
    </div>
  );
}
